import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Cropper, { Area } from 'react-easy-crop';
import styled from '@emotion/styled';
import { designSystem } from '@/styles/theme';
import ButtonSpinner from '@/components/common/elements/ButtonSpinner';

/** 안전 영역 — 가장자리에서 이만큼(비율) 안쪽. 카드·상세에서 잘리거나 배지에 가려지지 않는 범위 */
export const SAFE_AREA_INSET: string = '6%';

const OUTPUT_WIDTH: number = 1280;
const OUTPUT_HEIGHT: number = 720;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
`;

const ModalBox = styled.div`
  outline: none;
  background: #fff;
  border-radius: 12px;
  width: 100%;
  max-width: 560px;
  max-height: calc(100vh - 32px);
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.25);
`;

const Header = styled.div`
  padding: 16px 20px;
  font-size: 1.05rem;
  font-weight: 700;
  color: #1a1a1a;
  border-bottom: 1px solid #f0f0f0;
`;

const CropArea = styled.div`
  position: relative;
  width: 100%;
  height: 300px;
  background: #1a1a1a;
  flex: 0 0 auto;
  /* 폰에서 끌면 페이지가 아니라 사진이 움직이게(2026-09-29 사용자) */
  touch-action: none;
  overscroll-behavior: contain;

  /* 잘리는 틀 안쪽 6% 점선 = 글자를 둘 안전 영역 */
  .reactEasyCrop_CropArea::after {
    content: '';
    position: absolute;
    top: ${SAFE_AREA_INSET};
    bottom: ${SAFE_AREA_INSET};
    left: ${SAFE_AREA_INSET};
    right: ${SAFE_AREA_INSET};
    border: 1.5px dashed rgba(255, 255, 255, 0.95);
    box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.25);
    pointer-events: none;
  }

  @media (max-width: 768px) {
    height: 220px;
  }
`;

const Controls = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 20px 0;
`;

const ZoomLabel = styled.span`
  font-size: 0.85rem;
  color: #6b7280;
  white-space: nowrap;
`;

const ZoomSlider = styled.input`
  flex: 1;
  accent-color: ${designSystem.colors.primary};
`;

const Guide = styled.p`
  margin: 10px 20px 0;
  font-size: 0.85rem;
  line-height: 1.6;
  color: #4b5563;
  white-space: pre-line;
`;

const WarnText = styled.p`
  font-size: 0.8rem;
  color: #d97706;
  margin: 6px 20px 0;
`;

const PreviewRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 14px;
  padding: 14px 20px 0;
`;

const PreviewCaption = styled.div`
  font-size: 0.78rem;
  color: #6b7280;
  margin-bottom: 4px;
`;

const PreviewFrame = styled.div<{ width: number }>`
  width: 100%;
  max-width: ${({ width }) => width}px;
  aspect-ratio: 16 / 9;
  border-radius: ${({ width }) => (width > 160 ? 12 : 8)}px;
  overflow: hidden;
  background-color: #f3f4f6;
  background-repeat: no-repeat;
  border: 1px solid #e5e7eb;
`;

const Footer = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  padding: 16px 20px;
`;

const CancelBtn = styled.button`
  padding: 10px 20px;
  background: #f3f4f6;
  color: #374151;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
`;

const ApplyBtn = styled.button`
  padding: 10px 24px;
  background: ${designSystem.colors.primary};
  color: #fff;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;

  &:disabled {
    background: #e2e8f0;
    color: #94a3b8;
    cursor: not-allowed;
  }
`;

const loadImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (err) => reject(err));
    image.src = url;
  });

/** 원본에서 area 를 잘라 1280×720 JPEG File 로 만든다 */
const cropToFile = async (imageSrc: string, area: Area, fileName: string): Promise<File> => {
  const image = await loadImage(imageSrc);
  const canvas = document.createElement('canvas');
  canvas.width = OUTPUT_WIDTH;
  canvas.height = OUTPUT_HEIGHT;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas context not available');
  ctx.drawImage(image, area.x, area.y, area.width, area.height, 0, 0, OUTPUT_WIDTH, OUTPUT_HEIGHT);
  return new Promise<File>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Failed to create blob'));
          return;
        }
        const baseName: string = fileName.replace(/\.[^.]+$/, '');
        resolve(new File([blob], `${baseName}_16x9.jpg`, { type: 'image/jpeg' }));
      },
      'image/jpeg',
      0.92,
    );
  });
};

/**
 * 자르는 중인 영역을 16:9 상자에 그대로 보여 주는 배경 스타일.
 * 크기·위치를 % 로 잡아 상자 폭이 화면에 따라 줄어도 같은 영역이 보인다.
 */
const previewStyle = (
  imageSrc: string,
  area: Area | null,
  natural: { width: number; height: number } | null,
): React.CSSProperties => {
  if (!area || !natural || area.width === 0 || area.height === 0) return {};
  const percent = (offset: number, size: number, full: number): number =>
    full - size <= 0 ? 0 : (offset / (full - size)) * 100;
  return {
    backgroundImage: `url(${imageSrc})`,
    backgroundSize: `${(natural.width / area.width) * 100}% ${(natural.height / area.height) * 100}%`,
    backgroundPosition: `${percent(area.x, area.width, natural.width)}% ${percent(area.y, area.height, natural.height)}%`,
  };
};

interface ThumbnailCropModalProps {
  file: File | null;
  onCropped: (croppedFile: File) => void;
  onCancel: () => void;
}

/**
 * 세미나 썸네일 16:9 자르기 — 안쪽 6% 점선 안전 영역과
 * 잘린 결과를 목록 카드(작은 16:9)·상세 상단(큰 16:9) 두 모양으로 바로 보여 준다.
 */
const ThumbnailCropModal: React.FC<ThumbnailCropModalProps> = ({ file, onCropped, onCancel }) => {
  const [imageSrc, setImageSrc] = useState<string>('');
  const [natural, setNatural] = useState<{ width: number; height: number } | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [area, setArea] = useState<Area | null>(null);
  const [processing, setProcessing] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!file) {
      setImageSrc('');
      return undefined;
    }
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setArea(null);
    setNatural(null);
    setFailed(false);
    const url: string = URL.createObjectURL(file);
    setImageSrc(url);
    loadImage(url)
      .then((img) => setNatural({ width: img.naturalWidth, height: img.naturalHeight }))
      .catch(() => setNatural(null));
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const boxRef = useRef<HTMLDivElement>(null);

  // 열려 있는 동안 뒤 페이지가 스크롤되지 않게(폰에서 사진을 끌 때 페이지가 딸려 움직이던 문제)
  useEffect(() => {
    if (!imageSrc) return undefined;
    const { body, documentElement } = document;
    const prevBody: string = body.style.overflow;
    const prevHtml: string = documentElement.style.overflow;
    body.style.overflow = 'hidden';
    documentElement.style.overflow = 'hidden';
    return () => {
      body.style.overflow = prevBody;
      documentElement.style.overflow = prevHtml;
    };
  }, [imageSrc]);

  // 열리면 모달로 포커스를 옮기고, 닫히면 원래 있던 곳(파일 선택 버튼 등)으로 돌려준다
  useEffect(() => {
    if (!imageSrc) return undefined;
    const previous = document.activeElement as HTMLElement | null;
    boxRef.current?.focus();
    return () => previous?.focus?.();
  }, [imageSrc]);

  /** Esc 로 닫기 + Tab 포커스를 모달 안에 가둔다 */
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      onCancel();
      return;
    }
    if (e.key !== 'Tab' || !boxRef.current) return;
    const focusables = Array.from(
      boxRef.current.querySelectorAll<HTMLElement>('button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'),
    );
    if (focusables.length === 0) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement;
    if (e.shiftKey && (active === first || active === boxRef.current)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  };

  const onCropComplete = useCallback((_: Area, areaPixels: Area) => {
    setArea(areaPixels);
  }, []);

  const handleApply = async () => {
    if (!file || !area) return;
    setProcessing(true);
    setFailed(false);
    try {
      onCropped(await cropToFile(imageSrc, area, file.name));
    } catch (error) {
      console.error('썸네일 자르기 실패:', error);
      setFailed(true);
    } finally {
      setProcessing(false);
    }
  };

  if (!file || !imageSrc) return null;

  const lowRes: boolean = !!natural && (natural.width < OUTPUT_WIDTH || natural.height < OUTPUT_HEIGHT);

  // 본문(당겨서 새로고침·스와이프 뒤로가기로 transform 되는 곳) 밖, body 에 바로 띄워야 화면에 고정된다
  return createPortal(
    <Overlay onClick={onCancel} data-no-swipe-back>
      <ModalBox
        ref={boxRef}
        role="dialog"
        aria-modal="true"
        aria-label="썸네일 자르기"
        tabIndex={-1}
        onKeyDown={handleKeyDown}
        onClick={(e) => e.stopPropagation()}
      >
        <Header>썸네일 자르기 (16:9)</Header>
        <CropArea>
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={16 / 9}
            showGrid={false}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
            objectFit="contain"
          />
        </CropArea>
        <Controls>
          <ZoomLabel>확대</ZoomLabel>
          <ZoomSlider
            type="range"
            min={1}
            max={3}
            step={0.01}
            value={zoom}
            aria-label="확대"
            onChange={(e) => setZoom(Number(e.target.value))}
          />
        </Controls>
        <Guide>{'글자는 점선 안에 두세요.\n점선 밖은 화면에 따라 잘리거나 배지에 가려질 수 있어요.'}</Guide>
        {lowRes && (
          <WarnText>
            권장 크기({OUTPUT_WIDTH}×{OUTPUT_HEIGHT})보다 작은 이미지예요. 화질이 떨어질 수 있어요.
          </WarnText>
        )}
        <PreviewRow>
          <div style={{ flex: '0 0 136px' }}>
            <PreviewCaption>목록 카드</PreviewCaption>
            <PreviewFrame width={136} style={previewStyle(imageSrc, area, natural)} />
          </div>
          <div style={{ flex: '1 1 220px', maxWidth: 300 }}>
            <PreviewCaption>상세 화면</PreviewCaption>
            <PreviewFrame width={300} style={previewStyle(imageSrc, area, natural)} />
          </div>
        </PreviewRow>
        {failed && <WarnText>이미지를 자르지 못했어요. 다른 이미지로 다시 시도해 주세요.</WarnText>}
        <Footer>
          <CancelBtn type="button" onClick={onCancel}>
            취소
          </CancelBtn>
          <ApplyBtn type="button" onClick={handleApply} disabled={processing || !area}>
            {processing ? <ButtonSpinner /> : '적용'}
          </ApplyBtn>
        </Footer>
      </ModalBox>
    </Overlay>,
    document.body,
  );
};

export default ThumbnailCropModal;
